// The smooth line through the chart's daily scores. A monotone cubic: it bends between days
// but never overshoots them, so a curve never dips below a score the kid did not reach, and a
// run of equal days stays a flat line.

export interface Point {
  x: number;
  y: number;
}

const round = (v: number) => Math.round(v * 10) / 10;

/**
 * An SVG path through `points` (x increasing). Each point's slope is the steeper-limited mean of
 * its two neighbours' (Steffen's method), zero at a peak or a trough, which is what keeps every
 * segment between its two scores.
 */
export function monotonePath(points: readonly Point[]): string {
  const n = points.length;
  if (n === 0) return "";
  if (n === 1) return `M${round(points[0].x)} ${round(points[0].y)}`;

  const h: number[] = [];
  const s: number[] = [];
  for (let i = 0; i < n - 1; i++) {
    h.push(points[i + 1].x - points[i].x);
    s.push(h[i] === 0 ? 0 : (points[i + 1].y - points[i].y) / h[i]);
  }
  const m: number[] = [s[0]];
  for (let i = 1; i < n - 1; i++) {
    const p = (s[i - 1] * h[i] + s[i] * h[i - 1]) / (h[i - 1] + h[i]);
    m.push((Math.sign(s[i - 1]) + Math.sign(s[i])) * Math.min(Math.abs(s[i - 1]), Math.abs(s[i]), 0.5 * Math.abs(p)));
  }
  m.push(s[n - 2]);

  let d = `M${round(points[0].x)} ${round(points[0].y)}`;
  for (let i = 0; i < n - 1; i++) {
    const a = points[i];
    const b = points[i + 1];
    const t = h[i] / 3;
    d += `C${round(a.x + t)} ${round(a.y + m[i] * t)} ${round(b.x - t)} ${round(b.y - m[i + 1] * t)} ${round(b.x)} ${round(b.y)}`;
  }
  return d;
}
